import type { ReportDtoT } from '@mathflat/domain/@entities/(Content)/Report'
import type { AllGradeSubject } from '@mathflat/domain/@entities/SchoolSystem/schema'
import { addMonths, endOfMonth, isAfter, subMonths } from 'date-fns'
import { startOfMonth } from 'date-fns/esm'
import type { History } from 'history'
import { makeAutoObservable } from 'mobx'
import { generatePath } from 'react-router'
import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'
import { RouteName } from 'src/router'
import type { WebthflatLocationState } from 'src/router/state'

import { TOAST_STR } from '@/constant'
import { reportController } from '@/controller/report.controller'
import { smsController } from '@/controller/sms.controller'
import type { GroupInfo, Student } from '@/dto'
import { ReqParams } from '@/dto'
import WebviewService from '@/service/webview.service'
import type { DateStr } from '@/types/common'
import appPostMsg from '@/utils/appPostMsg'
import dateUtils from '@/utils/DateUtils'
import { fileDownload, urlPreview } from '@/utils/download'
import { commonErrorMessage, toastError } from '@/utils/toast'

import type { ReportModelV2 } from '../_model/report.model'

type ReportListParamT = {
  studentIds: Student['id'][]
  titleKeyword: string
  startDate: DateStr<'-'>
  endDate: DateStr<'-'>
  id: string
  showWithRealtime: boolean
  page: number
  size: number
  isLast?: boolean
}

export type ReportsV2Response = { status: 'DATA'; reportColl: CheckableColl<ReportModelV2, 'id'> }

export type ReportEmptyType = 'NO_DATA' | 'NO_DATA_WITHIN_RANGE' | 'NO_DATA_WITHIN_SEARCH' | 'DATA'

export default class ReportListService {
  groupInfo?: GroupInfo
  reports: { status: 'INIT' | 'NO_DATA' } | ReportsV2Response = { status: 'INIT' }
  realtimeSetting: ReportDtoT.GetReportRealTimeSettingV2Response | null = null

  filter = new ReqParams<ReportListParamT>({
    params: {
      studentIds: [],
      titleKeyword: '',
      showWithRealtime: true,
      startDate: dateUtils.toDateString(startOfMonth(new Date()), '-'),
      endDate: dateUtils.toDateString(endOfMonth(new Date()), '-'),
      page: 0,
      size: 20,
      isLast: false,
    },
  })

  // ui
  isLoading = false
  showPeriodPicker = false

  constructor() {
    makeAutoObservable(this)
  }

  get filteredReports() {
    if (this.reports.status !== 'DATA') {
      return []
    }

    const allReports = this.reports.reportColl.toArr
    const realTimeReports = allReports.filter((report) => report.type === 'REAL_TIME')
    const customReports = allReports.filter((report) => report.type !== 'REAL_TIME')

    // Custom 보고서를 생성일 기준 내림차순 정렬 (최신순)
    const sortedCustomReports = customReports.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA
    })

    // 실시간 보고서 제외 조건: 커스텀 기간이거나 showWithRealtime이 false인 경우
    if (this.isCustomDateRange || !this.filter.params.showWithRealtime) {
      return sortedCustomReports
    }

    // 실시간 보고서를 포함하는 경우: [실시간, ...커스텀(정렬)]
    return [...realTimeReports, ...sortedCustomReports]
  }

  get hasParamsChanged() {
    return !!this.filter.params.titleKeyword || !this.filter.params.showWithRealtime
  }

  get navigatableDates() {
    const startDate = dateUtils.fromDateStr(this.filter.params.startDate, '-')
    const nextMonth = startOfMonth(addMonths(startDate, 1))

    return {
      prev: startOfMonth(subMonths(startDate, 1)),
      current: startDate,
      next: isAfter(nextMonth, new Date()) ? null : nextMonth,
    }
  }

  get areAllReportStatusSuccess() {
    if (this.reports.status !== 'DATA') {
      return false
    }

    return this.reports.reportColl.toCheckedValues.every((report) => report.pdfStatus === 'SUCCESS')
  }

  get isCustomDateRange() {
    const start = dateUtils.fromDateStr(this.filter.params.startDate, '-')
    const end = dateUtils.fromDateStr(this.filter.params.endDate, '-')
    const isStartFirstDay = start.getDate() === 1
    const isEndLastDay = end.getDate() === endOfMonth(end).getDate()
    const isSameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
    return !(isSameMonth && isStartFirstDay && isEndLastDay)
  }

  getReportLocationDescriptor(
    params: { type: 'create' } | { type: 'update'; reportId: ReportModelV2['id'] },
  ): History.LocationDescriptor<WebthflatLocationState> | undefined {
    if (this.groupInfo) {
      return {
        pathname: generatePath(RouteName.createLessonReportStepOne, {
          groupType: this.groupInfo.data.type,
          key: this.groupInfo.key,
          ...(params.type === 'update' && { reportId: params.reportId }),
        }),
        state: { groupName: this.groupInfo.name, prevPath: window.location.pathname },
      }
    }
  }

  setGroupInfo(groupInfo: GroupInfo) {
    this.groupInfo = groupInfo
  }

  setCheckAll() {
    if (this.reports.status !== 'DATA') return

    const reportColl = this.reports.reportColl
    const checkableReports = reportColl.toArr.filter((report) => report.type !== 'REAL_TIME')
    const isAllCheckableChecked = checkableReports.every((report) =>
      reportColl.isChecked(report.id),
    )

    if (!isAllCheckableChecked) {
      checkableReports.forEach((report) => {
        reportColl.setCheck(report.id)
      })
    }
  }

  toggleCheckAllV2 = () => {
    if (this.reports.status !== 'DATA') return

    const reportColl = this.reports.reportColl
    const checkableReports = reportColl.toArr.filter((report) => report.type !== 'REAL_TIME')
    const isAllCheckableChecked = checkableReports.every((report) =>
      reportColl.isChecked(report.id),
    )

    if (!isAllCheckableChecked) {
      checkableReports.forEach((report) => {
        reportColl.setCheck(report.id)
      })
    } else {
      reportColl.clearAllCheck()
    }
  }

  clearAllCheckV2() {
    if (this.reports.status !== 'DATA') return
    this.reports.reportColl.clearAllCheck()
  }

  setCheckAllFailedReportsV2() {
    if (this.reports.status !== 'DATA') return

    const reportColl = this.reports.reportColl
    reportColl.clearAllCheck()
    reportColl.toArr.forEach((report) => {
      if (report.pdfStatus === 'FAIL') {
        reportColl.setCheck(report.id)
      }
    })
  }

  checkAnyCheckedReportsCloseToParents() {
    if (this.reports.status !== 'DATA') {
      return false
    }

    return this.reports.reportColl.toCheckedValues.some((report) => !report.sharedToParent)
  }

  deleteReports = async (reportIds: ReportModelV2['id'][]) => {
    try {
      await reportController.deleteReports(reportIds)
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  onReportScrollEnd = async () => {
    if (!this.filter.params.isLast) {
      this.filter.nextPage()
      await this.fetchV2Reports()
    }
  }

  /**
   * 특정 보고서들을 보고서 버전에 따라 분기하여 갱신합니다.
   * filteredReports에 있는 보고서만 갱신하며, 없는 ID는 무시합니다.
   */
  requestRefetchReportListItemsById = async (reportIds: ReportModelV2['id'][]) => {
    if (this.reports.status !== 'DATA') return

    const targetReports = this.filteredReports.filter((report) => reportIds.includes(report.id))
    if (targetReports.length === 0) return

    const v1ReportIds: number[] = []
    const v2ReportIds: number[] = []
    targetReports.forEach((report) => {
      if (report.type === 'REAL_TIME') {
        return
      }
      if (report.type === 'CUSTOM') {
        v2ReportIds.push(report.id)
      } else {
        v1ReportIds.push(report.id)
      }
    })

    await Promise.all([
      this._fetchAndUpdateV1Reports(v1ReportIds),
      this._fetchAndUpdateV2Reports(v2ReportIds),
    ])
  }

  private _fetchAndUpdateV1Reports = async (reportIds: number[]) => {
    if (reportIds.length === 0) return
    try {
      const reportColl = await reportController.fetchReportsById({ reportIds })
      const reports = this.reports
      if (reports.status !== 'DATA') return
      reportColl.toArr.forEach((simpleReport) => {
        const existingReport = reports.reportColl.get(simpleReport.id)
        if (existingReport) {
          existingReport.pdfStatus = simpleReport.status
        }
      })
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  private _fetchAndUpdateV2Reports = async (reportIds: number[]) => {
    if (reportIds.length === 0) return
    try {
      const fetchedReports = await reportController.fetchCustomReportsV2ById({ ids: reportIds })
      const reports = this.reports
      if (reports.status !== 'DATA') return
      fetchedReports.forEach((report) => {
        reports.reportColl.set(report.id, report)
      })
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  fetchNavigationRange = async (studentIds: req.RecentReport['studentIds']) => {
    const currentDateTime = await reportController.fetchNavigationRange({ studentIds })
    return { startDate: startOfMonth(currentDateTime), endDate: endOfMonth(currentDateTime) }
  }

  fetchShowReportToParents = async (
    reportIds: ReportModelV2['id'][],
    isOpened: boolean,
    sending: boolean,
  ) => {
    await reportController.fetchShowReportToParents({ reportIds, opened: isOpened, sending })
  }

  download = async (reportIds: ReportModelV2['id'][], groupName: GroupInfo['name']) => {
    try {
      const { presignedUrl, fileName } = await reportController.download({
        reportIds: reportIds,
        groupName: groupName,
        presignedUrlType: 'DOWNLOAD',
      })
      await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
    } catch (e) {
      Number(e.status) === 400 ? toastError(TOAST_STR.reportNotFound) : commonErrorMessage(e)
    }
  }

  downloadRealTime = async (report: ReportModelV2) => {
    try {
      const { presignedUrl, fileName } = await reportController.downloadV2ReportRealTime({
        studentId: report.studentId,
        year: report.year,
        month: report.month,
        presignedUrlType: 'DOWNLOAD',
      })
      await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
    } catch (e) {
      Number(e.status) === 400 ? toastError(TOAST_STR.reportNotFound) : commonErrorMessage(e)
    }
  }

  export(reportIds: ReportModelV2['id'][], groupName: GroupInfo['name']) {
    appPostMsg.exportReport({
      reportIds,
      groupName,
    })
  }

  exportRealTime(report: ReportModelV2) {
    appPostMsg.exportRealTimeReport({
      studentId: report.studentId,
      year: report.year,
      month: report.month,
    })
  }

  preview = async (reportId: ReportModelV2['id'], groupName: GroupInfo['name']) => {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewReport(reportId)
        return
      }

      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await reportController.download({
          reportIds: [reportId],
          groupName,
          presignedUrlType: 'PREVIEW',
        })
        return { url: presignedUrl, fullFileName: fileName }
      }

      await urlPreview(getPreviewUrl)
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  previewRealTime = async (report: ReportModelV2) => {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewRealtimeReport({
          studentId: report.studentId,
          year: report.year,
          month: report.month,
        })

        return
      }

      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await reportController.downloadV2ReportRealTime({
          studentId: report.studentId,
          year: report.year,
          month: report.month,
          presignedUrlType: 'PREVIEW',
        })
        return { url: presignedUrl, fullFileName: fileName }
      }

      await urlPreview(getPreviewUrl)
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  fetchReportSmsSample = async () => {
    if (this.reports.status !== 'DATA') return

    const studentIdList = this.reports.reportColl.toCheckedValues.map((report) => report.studentId)

    if (studentIdList.length > 0) {
      return smsController.fetchAttendanceSmsSample({
        templateSendingType: 'REPORT',
        studentIdList,
        link: 'https:// (이하 링크)',
      })
    }
  }

  shareCustomReportsToParentApp = async (params: {
    reportIds: number[]
    shouldSendSmsToParent: boolean
  }) => {
    await reportController.shareV2ReportCustomToParentApp(params)
  }

  unshareCustomReportsToParentApp = async (reportIds: number[]) => {
    await reportController.unshareV2ReportCustomToParentApp({ reportIds })
  }

  shareCustomReportsToParentSms = async (reportIds: number[]) => {
    await reportController.shareV2ReportCustomToParentSms({ reportIds })
  }

  shareRealTimeReportToParentApp = async (params: {
    studentId: string
    year: number
    month: number
    shouldSendSmsToParent: boolean
  }) => {
    await reportController.shareReportRealTimeToParentApp(params)
  }

  shareRealTimeReportToParentSms = async (params: {
    studentId: string
    year: number
    month: number
  }) => {
    await reportController.shareReportRealTimeToParentSms(params)
  }

  shareCustomReportByLink = async (reportId: number) => {
    const result = await reportController.shareV2ReportCustomByLink({ reportId })
    return result
  }

  shareRealTimeReportByLink = async (params: {
    studentId: string
    year: number
    month: number
  }) => {
    const result = await reportController.shareReportRealTimeByLink(params)
    return result
  }

  async downloadOrExport(uuid: req.OneClickGroupReportDownloadParams['uuid']) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.exportGroupOneClickReport({ key: uuid })
      } else {
        const { presignedUrl, fileName } = await reportController.downloadOneClickGroup({
          uuid: uuid,
        })
        await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
      }
    } catch (e) {
      // TODO: commonError 객체에 제대로 감싸져 오지 않음.
      commonErrorMessage(e)
    }
  }

  async downloadOrExportCas(uuid: req.CasOneClickGroupReportDownloadParams['uuid']) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.exportGroupCasOneClickReport({ key: uuid })
      } else {
        const { presignedUrl, fileName } = await reportController.downloadCasOneClickGroup({
          uuid: uuid,
        })
        await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
      }
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  async initializeWithStudentIds(studentIds: Student['id'][]) {
    const dateParams = await this.fetchNavigationRange(studentIds)
    this.filter.set({
      startDate: dateUtils.toDateString(dateParams.startDate, '-'),
      endDate: dateUtils.toDateString(dateParams.endDate, '-'),
      page: 0,
    })
  }

  initializeWithDates(startDate: Date, endDate: Date) {
    this.filter.set({
      startDate: dateUtils.toDateString(startDate, '-'),
      endDate: dateUtils.toDateString(endDate, '-'),
      page: 0,
    })
  }

  async fetchV2Reports() {
    if (!this.groupInfo) return
    if (!this.filter.params.startDate || !this.filter.params.endDate) return

    this.isLoading = true
    try {
      const realTimeParams = this._buildV2RealTimeRequestParams(this.groupInfo)
      const customParams = this._buildV2CustomRequestParams(this.groupInfo)

      const [realTimeReports, customResponse] = await Promise.all([
        reportController.fetchV2ReportRealTime(realTimeParams),
        reportController.fetchV2ReportCustom(customParams),
      ])

      // Custom 페이지네이션 정보 업데이트
      this.filter.setLastPage(customResponse.isLast)

      // 페이지가 0이면 새로 시작, 0보다 크면 기존 데이터에 추가
      if (this.filter.params.page === 0) {
        // 초기 로딩: 실시간 + 커스텀
        const reports = [...realTimeReports, ...customResponse.content]

        if (reports.length === 0) {
          this.reports = { status: 'NO_DATA' }
        } else {
          const reportMap = CheckableMap.fromArr(reports, 'id')
          const reportColl = new CheckableColl<ReportModelV2, 'id'>(reportMap)
          this.reports = { status: 'DATA', reportColl }
          await this._loadRealTimeMetricsIfNeeded()
        }
      } else {
        // 추가 로딩: 커스텀만 추가 (실시간은 이미 로드됨)
        if (this.reports.status === 'DATA' && customResponse.content.length > 0) {
          const reportColl = this.reports.reportColl
          customResponse.content.forEach((report) => {
            reportColl.set(report.id, report)
          })
        }
      }
    } catch (e) {
      commonErrorMessage(e)
      this.reports = { status: 'NO_DATA' }
    } finally {
      this.isLoading = false
    }
  }

  async fetchRealtimeSetting(groupInfo: GroupInfo) {
    try {
      const studentId = groupInfo.data.type === 'student' ? groupInfo.data.studentId : null
      const targetDate = this.filter.params.startDate
        ? dateUtils.fromDateStr(this.filter.params.startDate, '-')
        : new Date()
      const year = targetDate.getFullYear()
      const month = targetDate.getMonth() + 1
      const setting = await reportController.fetchV2ReportRealTimeSetting({
        studentId,
        year,
        month,
      })
      this.realtimeSetting = setting
    } catch (error) {
      this.realtimeSetting = null
    }
  }

  async updateRealtimeSetting(
    settingData: Omit<
      ReportDtoT.PutReportRealTimeSettingV2Request,
      'studentGroupKey' | 'schoolType' | 'grade' | 'lessonClassId' | 'studentId'
    >,
  ) {
    if (!this.groupInfo) return

    const groupInfo = this.groupInfo
    const groupType = groupInfo.data.type

    const requestBody = (() => {
      if (groupType === 'student') {
        return {
          ...settingData,
          studentGroupKey: 'STUDENT' as const,
          studentId: groupInfo.data.studentId,
        }
      } else if (groupType === 'grade') {
        const schoolGrade = groupInfo.data.schoolGrade
        if (!schoolGrade) return null

        return {
          ...settingData,
          studentGroupKey: 'GRADE' as const,
          schoolType: schoolGrade.schoolType,
          grade: String(schoolGrade.grade) as AllGradeSubject,
        }
      } else if (groupType === 'lesson-class') {
        return {
          ...settingData,
          studentGroupKey: 'LESSON_CLASS' as const,
          lessonClassId: groupInfo.data.classId,
        }
      }
      return null
    })()

    if (!requestBody) return

    await reportController.updateV2ReportRealTimeSetting(requestBody)
    await this.fetchRealtimeSetting(this.groupInfo)
  }

  private _buildV2RealTimeRequestParams(
    groupInfo: GroupInfo,
  ): ReportDtoT.GetReportRealTimeV2Request {
    const { startDate, endDate, titleKeyword } = this.filter.params
    const baseParams = { startDate, endDate, searchKey: titleKeyword || null }
    if (groupInfo.data.type === 'student') {
      return {
        ...baseParams,
        studentId: groupInfo.data.studentId,
        studentGroupKey: 'STUDENT',
      }
    } else if (groupInfo.data.type === 'lesson-class') {
      return {
        ...baseParams,
        lessonClassId: groupInfo.data.classId,
        studentGroupKey: 'LESSON_CLASS',
      }
    } else {
      return {
        ...baseParams,
        schoolType: groupInfo.data.schoolGrade.schoolType,
        grade: String(groupInfo.data.schoolGrade.grade) as AllGradeSubject,
        studentGroupKey: 'GRADE',
      }
    }
  }

  private _buildV2CustomRequestParams(groupInfo: GroupInfo): ReportDtoT.GetReportCustomV2Request {
    const { startDate, endDate, titleKeyword, page, size } = this.filter.params
    const baseParams = { startDate, endDate, searchKey: titleKeyword || null, page, size }
    if (groupInfo.data.type === 'student') {
      return {
        ...baseParams,
        studentId: groupInfo.data.studentId,
        studentGroupKey: 'STUDENT',
      }
    } else if (groupInfo.data.type === 'lesson-class') {
      return {
        ...baseParams,
        lessonClassId: groupInfo.data.classId,
        studentGroupKey: 'LESSON_CLASS',
      }
    } else {
      return {
        ...baseParams,
        schoolType: groupInfo.data.schoolGrade.schoolType,
        grade: String(groupInfo.data.schoolGrade.grade) as AllGradeSubject,
        studentGroupKey: 'GRADE',
      }
    }
  }

  private async _loadRealTimeMetricsIfNeeded() {
    if (this.isCustomDateRange || this.groupInfo?.data.type !== 'student') return
    if (this.reports.status !== 'DATA') return
    const realTimeReports = this.reports.reportColl.toArr.filter((r) => r.type === 'REAL_TIME')
    await Promise.all(
      realTimeReports.map(async (report) => {
        const metric = await reportController.fetchV2ReportRealTimeMetric({
          studentId: report.studentId,
          year: report.year,
          month: report.month,
        })
        report.setMetric(metric)
      }),
    )
  }
}

export function assertReportsV2DataExist(
  reportsV2: ReportListService['reports'],
): asserts reportsV2 is ReportsV2Response {
  if (reportsV2.status !== 'DATA') {
    throw Error
  }
}
